<template>
  <div
    class="card supplier-offer tw-border tw-border-solid tw-border-gray-300 bgm-white"
    :class="{ quiet: isWithdrawn }"
  >
    <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
    <BaseShowMore :collapsed-height="165" fade collapsable>
      <div class="card-body tw-relative">
        <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
        <WdsStack direction-row gap-md class="tw-mb-3">
          <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
          <WdsAvatar md :initials="initials" :path="getAvatarPath(avatarUrl, freelancerGlobalId)" />
          <div class="tw-flex tw-justify-between tw-w-full tw-items-center">
            <div class="tw-flex tw-items-center tw-gap-2">
              <slot name="title"></slot>
              <CandidateStageLabel :step="stage" data-test="supplier-offer-stage" />
            </div>
            <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
            <WdsButton
              v-if="canHire"
              :data-test="`hire-submitted-offer-${offerId}`"
              @click="goToHire"
            >
              {{ $t('Offer Job') }}
            </WdsButton>
          </div>
        </WdsStack>
        <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
        <WdsDivider class="divider tw-left-0 tw-w-full" />
        <div class="tw-mt-6">
          <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
          <SupplierOfferDataItem icon-name="calendar" :text="availableText"></SupplierOfferDataItem>
          <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
          <SupplierOfferDataItem icon-name="money" :text="workerRateLabel">
            <div v-if="previousRate">
              <span class="tw-text-gray-600 tw-line-through tw-mr-2">{{ previousRate }}</span>
              <span class="tw-text-green-500">{{ rate }}</span>
            </div>
          </SupplierOfferDataItem>
          <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
          <SupplierOfferDataItem
            v-if="referralFee"
            icon-name="tag"
            :text="`${$t('Referral fee')}: ${referralFee}`"
          ></SupplierOfferDataItem>
          <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
          <SupplierOfferDataItem
            v-if="submissionType"
            icon-name="user"
            :text="`${$t('Submission type')}: ${submissionType}`"
          ></SupplierOfferDataItem>
          <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
          <SupplierOfferDataItem v-for="file in files" :key="file.id" icon-name="file">
            <a target="_blank" :href="file.url">
              {{ file.name }}
            </a>
          </SupplierOfferDataItem>
          <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
          <SupplierOfferDataItem v-for="link in parsedLinks" :key="link.url" icon-name="link">
            <a target="_blank" class="tw-overflow-clip" :href="link.url">{{ link.value }}</a>
          </SupplierOfferDataItem>
          <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
          <SupplierOfferDataItem v-if="brief" icon-name="chat" :text="brief">
            <template #icon>
              <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
              <WdsIcon class="tw-mt-1 tw-text-gray-600 tw-flex-shrink-0" name="chat" :size="16" />
            </template>
          </SupplierOfferDataItem>
        </div>
      </div>
      <template #button="{ buttonText }">
        <div class="tw-flex tw-justify-center tw-mb-4">
          <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
          <BaseShowMoreButton :button-text="buttonText" />
        </div>
      </template>
    </BaseShowMore>
  </div>
</template>

<script>
import { WdsAvatar, WdsButton, WdsStack, WdsDivider, WdsIcon } from '@worksome/wds-components'
import moment from 'moment'
import SupplierOfferDataItem from './SupplierOfferDataItem.vue'
import CandidateStageLabel from '@/components/Job/Candidates/CandidateStageLabel.vue'
import { BidStatus } from '@/utils/enum'
import Urls from '@/utils/urls'
import { getAvatarPath } from '@/utils/avatar'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'

export default {
  name: 'SupplierOffer',
  components: {
    WdsStack,
    WdsAvatar,
    WdsButton,
    WdsDivider,
    WdsIcon,
    SupplierOfferDataItem,
    CandidateStageLabel,
  },
  props: {
    companyIsAuth: {
      type: Boolean,
    },
    freelancerGlobalId: {
      type: String,
      required: true,
    },
    initials: {
      type: String,
      required: true,
    },
    avatarUrl: {
      type: String,
      default: () => null,
    },
    brief: {
      type: [String, null],
      default: () => null,
    },
    offerId: {
      type: Number,
      default: null,
    },
    yjobId: {
      type: Number,
      default: null,
    },
    status: {
      type: String,
      required: true,
    },
    links: {
      type: Array,
      default: () => [],
    },
    files: {
      type: Array,
      default: () => [],
    },
    previousRate: {
      type: String,
      default: undefined,
    },
    rate: {
      type: String,
      required: true,
    },
    availableFrom: {
      type: String,
      default: undefined,
    },
    canHire: {
      type: Boolean,
    },
    /** Pre-formatted referral fee string (e.g. "EUR 5.00/hour" or "5.00%"); empty when no fee. */
    referralFee: {
      type: String,
      default: '',
    },
    /** Localised submission type label ("Managed" / "Referred"); empty when not set on the offer. */
    submissionType: {
      type: String,
      default: '',
    },
    /** The candidate's hiring stage, as the GraphQL enum name. */
    stage: {
      type: String,
      default: '',
    },
  },
  data() {
    const availableText = this.availableFrom
      ? $t('Available from :date', {
          date: moment(this.availableFrom).format('YYYY-MM-DD'),
        })
      : $t('Available now')

    return {
      show: false,
      availableText,
      chevronClass: 'chevron-down',
    }
  },
  computed: {
    isOpen() {
      return this.status === BidStatus.OPEN
    },
    /** Accepted is the successful outcome — only fade submissions that went nowhere. */
    isWithdrawn() {
      return !this.isOpen && this.status !== BidStatus.ACCEPTED
    },
    /**
     * When a referral fee is also surfaced, prefix the rate with "Worker rate:"
     * so the two values aren't conflated; otherwise show the bare rate (the
     * legacy/no-fee case) to avoid an extra label on classic submissions.
     */
    workerRateLabel() {
      return this.referralFee ? `${$t('Worker rate')}: ${this.rate}` : this.rate
    },
    parsedLinks() {
      return this.links?.map((link) => ({
        value: link,
        url: Urls.formatWithProtocol(link),
      }))
    },
  },
  methods: {
    getAvatarPath,
    toggleShowMore() {
      this.chevronClass = this.chevronClass === 'chevron-down' ? 'chevron-up' : 'chevron-down'
    },
    goToHire() {
      window.location = laravelNamedRoute(namedRoutes.frontend.user.profile.hiredirect_offer, {
        freelancer: this.freelancerGlobalId,
        job: this.yjobId,
        offer: this.offerId,
      })
    },
  },
}
</script>

<style lang="scss">
.supplier-offer {
  border: 1px solid #dfdfe6;
  border-radius: 4px;

  &.card {
    margin-bottom: 12px;
  }

  .card-body {
    padding: 16px 20px;
    border-radius: 4px;
  }

  .brief {
    margin-top: 5px;
    padding: 8px 12px;
  }

  .divider {
    position: absolute !important;
  }

  .chevron-down {
    transform: rotate(0deg);
    transition: transform 200ms linear;
  }

  .chevron-up {
    transform: rotate(180deg);
    transition: transform 200ms linear;
  }
}
</style>
